import React, { useState, useEffect, useCallback } from 'react';
import { Camera, FileSignature, FileText, X, Trash2, ChevronLeft, ChevronRight, Loader, Image, Filter } from 'lucide-react';
import { mediaAttachmentService } from '../../../../services/mediaAttachmentService';
import { MediaAttachmentDto, MediaSearchResult } from '../../../../types/mediaAttachment';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useOperatorDisplayNames } from '../../../../hooks/useOperatorDisplayNames';

interface MediaLibraryProps {
  onBack?: () => void;
}

const ENTITY_TYPES = ['Order', 'POrder', 'Bilmottak', 'PList', 'InventoryCorrection', 'InventoryCounting', 'PutAway', 'Packing', 'WarehouseTransfer', 'Item'];
const MEDIA_TYPES = ['photo', 'signature', 'document'];
const PAGE_SIZE = 20;

function getMediaIcon(mediaType: string) {
  switch (mediaType) {
    case 'signature': return FileSignature;
    case 'document': return FileText;
    default: return Camera;
  }
}

function getMediaIconColor(mediaType: string) {
  switch (mediaType) {
    case 'signature': return 'text-purple-500 bg-purple-50';
    case 'document': return 'text-orange-500 bg-orange-50';
    default: return 'text-blue-500 bg-blue-50';
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const MediaLibrary: React.FC<MediaLibraryProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { getDisplayName } = useOperatorDisplayNames();
  const [searchResult, setSearchResult] = useState<MediaSearchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Lightbox
  const [lightboxAttachment, setLightboxAttachment] = useState<MediaAttachmentDto | null>(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [fullAttachment, setFullAttachment] = useState<MediaAttachmentDto | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await mediaAttachmentService.searchAttachments({
        entityType: entityTypeFilter || undefined,
        mediaType: mediaTypeFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        page,
        pageSize: PAGE_SIZE
      });
      setSearchResult(result);
    } catch (err) {
      console.error('Failed to search media:', err);
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, mediaTypeFilter, fromDate, toDate, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [entityTypeFilter, mediaTypeFilter, fromDate, toDate]);

  const handleOpenLightbox = async (att: MediaAttachmentDto) => {
    setLightboxAttachment(att);

    // If no dataUrl, fetch the full attachment with content
    if (!att.dataUrl) {
      try {
        setLightboxLoading(true);
        const full = await mediaAttachmentService.getAttachment(att.id);
        setFullAttachment(full);
      } catch (err) {
        console.error('Failed to load full attachment:', err);
      } finally {
        setLightboxLoading(false);
      }
    } else {
      setFullAttachment(att);
    }
  };

  const handleCloseLightbox = () => {
    setLightboxAttachment(null);
    setFullAttachment(null);
    setLightboxLoading(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('media.deleteConfirm', {}, 'Slett dette vedlegget?'))) return;

    try {
      await mediaAttachmentService.deleteAttachment(id);
      handleCloseLightbox();
      await loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const totalPages = searchResult ? Math.ceil(searchResult.totalCount / PAGE_SIZE) : 0;
  const items = searchResult?.items || [];
  const startItem = (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, searchResult?.totalCount || 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Image className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">{t('media.library', {}, 'Mediebibliotek')}</h2>
          {searchResult && (
            <span className="text-sm text-gray-500">
              ({searchResult.totalCount} {t('media.photos', {}, 'vedlegg')})
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">{t('media.filterByType', {}, 'Filtrer')}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <select
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('manager.modules.adminHost.mediaLibrary.allEntities', {}, 'Alle entiteter')}</option>
            {ENTITY_TYPES.map(et => (
              <option key={et} value={et}>{et}</option>
            ))}
          </select>

          <select
            value={mediaTypeFilter}
            onChange={(e) => setMediaTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('media.allTypes', {}, 'Alle typer')}</option>
            {MEDIA_TYPES.map(mt => (
              <option key={mt} value={mt}>
                {mt === 'photo' ? t('media.photo', {}, 'Foto') :
                 mt === 'signature' ? t('media.signature', {}, 'Signatur') :
                 t('media.document', {}, 'Dokument')}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder={t('manager.modules.adminHost.mediaLibrary.fromDate', {}, 'Fra dato')}
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder={t('manager.modules.adminHost.mediaLibrary.toDate', {}, 'Til dato')}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{t('media.noAttachments', {}, 'Ingen vedlegg funnet')}</p>
        </div>
      ) : (
        <>
          {/* Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {items.map((att) => {
              const Icon = getMediaIcon(att.mediaType);
              const iconColor = getMediaIconColor(att.mediaType);

              return (
                <button
                  key={att.id}
                  onClick={() => handleOpenLightbox(att)}
                  className="bg-white rounded-lg border hover:border-blue-400 hover:shadow-md transition-all overflow-hidden text-left group"
                >
                  {/* Thumbnail area */}
                  <div className="aspect-square relative overflow-hidden bg-gray-50">
                    {att.dataUrl ? (
                      <img
                        src={att.dataUrl}
                        alt={att.description || att.fileName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${iconColor}`}>
                        <Icon className="w-10 h-10" />
                      </div>
                    )}
                    {/* Media type badge */}
                    <span className="absolute top-1 right-1 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded">
                      {att.mediaType}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-700 truncate">
                      {att.entityType} #{att.entityKey}
                    </p>
                    {att.description && (
                      <p className="text-xs text-gray-500 truncate">{att.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {new Date(att.created).toLocaleDateString('nb-NO')}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-lg border px-4 py-3">
              <span className="text-sm text-gray-600">
                {t('manager.modules.adminHost.mediaLibrary.paginationRange', { '0': startItem, '1': endItem, '2': searchResult?.totalCount }, `${startItem}-${endItem} av ${searchResult?.totalCount}`)}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 px-2">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Lightbox */}
      {lightboxAttachment && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-2xl">
            {/* Close button */}
            <button
              onClick={handleCloseLightbox}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
              {/* Image area */}
              <div className="bg-gray-50 min-h-[200px] flex items-center justify-center">
                {lightboxLoading ? (
                  <Loader className="w-8 h-8 animate-spin text-blue-500" />
                ) : fullAttachment?.dataUrl ? (
                  <img
                    src={fullAttachment.dataUrl}
                    alt={fullAttachment.description || fullAttachment.fileName}
                    className="w-full max-h-[70vh] object-contain"
                  />
                ) : (
                  <div className="py-16 text-gray-400 flex flex-col items-center">
                    {React.createElement(getMediaIcon(lightboxAttachment.mediaType), { className: 'w-16 h-16 mb-2' })}
                    <p className="text-sm">{lightboxAttachment.fileName}</p>
                  </div>
                )}
              </div>

              {/* Info bar */}
              <div className="p-4 border-t space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {lightboxAttachment.entityType} #{lightboxAttachment.entityKey}
                    </p>
                    {lightboxAttachment.description && (
                      <p className="text-sm text-gray-600">{lightboxAttachment.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(lightboxAttachment.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                    title={t('common.buttons.delete', {}, 'Slett')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span>{new Date(lightboxAttachment.created).toLocaleString('nb-NO')}</span>
                  <span>{getDisplayName(lightboxAttachment.operatorID)}</span>
                  <span>{formatFileSize(lightboxAttachment.fileSize)}</span>
                  <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{lightboxAttachment.mediaType}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
